import { useCallback, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useReviews } from '../hooks/useApi';
import { SearchInput } from '../components/ui/SearchInput';
import { FilterDropdown, type FilterOption } from '../components/ui/FilterDropdown';
import {
  Star,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Eye,
  Search,
  ClipboardCheck,
  ArrowUpDown,
  AlertTriangle,
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { SkeletonTable, SkeletonCardGrid } from '../components/ui/Skeleton';
import type { RecommendationLevel } from '../types';
import { ReviewListCard } from '../components/ReviewListCard';

const recommendationOptions: FilterOption[] = [
  { value: '1', label: '1 Star - Not Recommended' },
  { value: '2', label: '2 Stars - Below Average' },
  { value: '3', label: '3 Stars - Average' },
  { value: '4', label: '4 Stars - Above Average' },
  { value: '5', label: '5 Stars - Highly Recommended' },
];

const recommendationLabels: Record<RecommendationLevel, string> = {
  1: 'Not Recommended',
  2: 'Below Average',
  3: 'Average',
  4: 'Above Average',
  5: 'Highly Recommended',
};

const recommendationColors: Record<RecommendationLevel, string> = {
  1: 'bg-red-100 text-red-800',
  2: 'bg-orange-100 text-orange-800',
  3: 'bg-yellow-100 text-yellow-800',
  4: 'bg-blue-100 text-blue-800',
  5: 'bg-green-100 text-green-800',
};

// Sort options for mobile dropdown
const sortOptions: FilterOption[] = [
  { value: 'created:desc', label: 'Newest First' },
  { value: 'created:asc', label: 'Oldest First' },
  { value: 'recommendation:desc', label: 'Highest Rated' },
  { value: 'recommendation:asc', label: 'Lowest Rated' },
  { value: 'score:desc', label: 'Best Score' },
  { value: 'candidate:asc', label: 'Name A-Z' },
];

// Star rating component
function StarRating({ rating, size = 'md' }: { rating: RecommendationLevel; size?: 'sm' | 'md' }) {
  const sizeClass = size === 'sm' ? 'h-3 w-3' : 'h-4 w-4';
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${sizeClass} ${
            star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
          }`}
        />
      ))}
    </div>
  );
}

// Score display component — overallScore is 0-50 (score5 * 10), display as X.X/5
function ScoreDisplay({ score }: { score: number }) {
  const score5 = score / 10;
  const percentage = score * 2; // 0-100 for color thresholds
  const getColor = () => {
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 60) return 'text-blue-600';
    if (percentage >= 40) return 'text-amber-600';
    return 'text-red-600';
  };

  return (
    <span className={`text-sm font-medium ${getColor()}`}>
      {score5.toFixed(1)}/5
    </span>
  );
}

export default function Reviews() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Extract filter values from URL
  const search = searchParams.get('search') || '';
  const recommendation = searchParams.get('recommendation') || '';
  const hasScreeningKnockout = searchParams.get('hasScreeningKnockout') === 'true';
  const staleFlag = searchParams.get('staleFlag') === 'true';
  const page = parseInt(searchParams.get('page') || '1', 10);
  const limit = 15;

  // Update URL params and reset page when filters change
  const updateFilters = useCallback(
    (updates: Record<string, string>) => {
      const newParams = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      // Reset page to 1 when filters change (unless we're just changing page)
      if (!('page' in updates)) {
        newParams.set('page', '1');
      }
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      updateFilters({ search: value });
    },
    [updateFilters]
  );

  const handleRecommendationChange = useCallback(
    (value: string) => {
      updateFilters({ recommendation: value });
    },
    [updateFilters]
  );

  const handleScreeningKnockoutChange = useCallback(() => {
    updateFilters({ hasScreeningKnockout: hasScreeningKnockout ? '' : 'true' });
  }, [updateFilters, hasScreeningKnockout]);

  const handleStaleFlagChange = useCallback(() => {
    updateFilters({ staleFlag: staleFlag ? '' : 'true' });
  }, [updateFilters, staleFlag]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      updateFilters({ page: String(newPage) });
    },
    [updateFilters]
  );

  const { data, isLoading, isFetching, error, refetch } = useReviews({
    page,
    limit,
    search: search || undefined,
    recommendation: recommendation ? parseInt(recommendation, 10) : undefined,
    hasScreeningKnockout: hasScreeningKnockout || undefined,
    staleFlag: staleFlag || undefined,
  });

  // Mobile sort state
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
    key: 'created',
    direction: 'desc',
  });

  const handleMobileSort = (value: string) => {
    const [key, direction] = value.split(':');
    setSortConfig({ key, direction: direction as 'asc' | 'desc' });
  };

  const mobileSortValue = `${sortConfig.key}:${sortConfig.direction}`;

  const hasActiveFilters = search || recommendation || hasScreeningKnockout || staleFlag;

  const clearFilters = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  if (error) {
    return (
      <div className="card p-6">
        <div className="text-center">
          <p className="text-red-600">Error loading reviews: {error.message}</p>
          <button
            onClick={() => refetch()}
            className="btn btn-primary mt-4"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900">AI Reviews</h2>
          <p className="text-sm text-gray-500">
            {data?.pagination.total ?? 0} reviews
            {hasActiveFilters && ' (filtered)'}
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="btn btn-secondary"
          disabled={isLoading}
        >
          <RefreshCw
            className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`}
          />
          Refresh
        </button>
      </div>

      {/* Search and Filters */}
      <div className="card p-4 overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-end gap-2 sm:gap-4">
          <div className="w-full sm:flex-1 sm:min-w-[200px]">
            <SearchInput
              value={search}
              onChange={handleSearchChange}
              placeholder="Search by candidate name..."
              label="Search"
              isLoading={isFetching}
            />
          </div>
          <div className="w-full sm:w-auto">
            <FilterDropdown
              label="Recommendation"
              options={recommendationOptions}
              value={recommendation}
              onChange={handleRecommendationChange}
              allOptionLabel="All Recommendations"
              size="md"
              fullWidth
              className="sm:w-auto"
            />
          </div>
          <div className="w-full sm:w-auto flex items-center min-h-[44px]">
            <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-gray-700">
              <input
                type="checkbox"
                checked={hasScreeningKnockout}
                onChange={handleScreeningKnockoutChange}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <AlertTriangle className="h-4 w-4 text-orange-500" />
              Screening Conflict
            </label>
          </div>
          <div className="w-full sm:w-auto flex items-center min-h-[44px]">
            <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-gray-700">
              <input
                type="checkbox"
                checked={staleFlag}
                onChange={handleStaleFlagChange}
                className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
              />
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Needs Re-review
            </label>
          </div>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="btn btn-secondary text-sm w-full sm:w-auto min-h-[44px]"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Mobile Card View (< 640px) */}
      <div className="sm:hidden space-y-4">
        {/* Mobile Sort Dropdown */}
        <div className="flex items-center gap-2">
          <ArrowUpDown className="h-4 w-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
          <FilterDropdown
            label=""
            options={sortOptions}
            value={mobileSortValue}
            onChange={handleMobileSort}
            includeAllOption={false}
            size="md"
            fullWidth
          />
        </div>

        {isLoading ? (
          <SkeletonCardGrid cards={4} columns={1} />
        ) : data?.data.length === 0 ? (
          <div className="card p-12 text-center">
            {hasActiveFilters ? (
              <>
                <Search className="mx-auto h-8 w-8 text-gray-400" />
                <p className="mt-2 text-gray-500">No reviews match your search criteria</p>
                <button
                  onClick={clearFilters}
                  className="mt-3 text-sm text-primary-600 hover:text-primary-700"
                >
                  Clear filters
                </button>
              </>
            ) : (
              <>
                <ClipboardCheck className="mx-auto h-8 w-8 text-gray-400" />
                <p className="mt-2 text-gray-500">No reviews found</p>
                <p className="text-sm text-gray-400">
                  Reviews will appear here once applications are processed by AI.
                </p>
              </>
            )}
          </div>
        ) : (
          data?.data.map((review) => (
            <ReviewListCard
              key={review.id}
              review={review}
              onView={(id) => navigate(`/reviews/${id}`)}
            />
          ))
        )}

        {/* Mobile Pagination */}
        {data && data.pagination.totalPages > 1 && (
          <div className="flex flex-col gap-3 bg-white rounded-lg p-4 shadow-sm border border-gray-200">
            <div className="text-sm text-gray-500 text-center">
              Showing{' '}
              <span className="font-medium">{(page - 1) * limit + 1}</span> to{' '}
              <span className="font-medium">
                {Math.min(page * limit, data.pagination.total)}
              </span>{' '}
              of <span className="font-medium">{data.pagination.total}</span>
            </div>
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => handlePageChange(Math.max(1, page - 1))}
                disabled={page === 1}
                className="btn btn-secondary flex-1 disabled:opacity-50"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </button>
              <button
                onClick={() => handlePageChange(Math.min(data.pagination.totalPages, page + 1))}
                disabled={page === data.pagination.totalPages}
                className="btn btn-secondary flex-1 disabled:opacity-50"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Table View - tablet+ (>= 640px) */}
      <div className="hidden sm:block card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Review
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Recommendation
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Overall Score
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Summary
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Model
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Created
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {isLoading ? (
              <tr>
                <td colSpan={7} className="p-0">
                  <SkeletonTable rows={5} columns={7} showHeader={false} />
                </td>
              </tr>
            ) : data?.data.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center">
                  {hasActiveFilters ? (
                    <>
                      <Search className="mx-auto h-8 w-8 text-gray-400" />
                      <p className="mt-2 text-gray-500">
                        No reviews match your search criteria
                      </p>
                      <button
                        onClick={clearFilters}
                        className="mt-3 text-sm text-primary-600 hover:text-primary-700"
                      >
                        Clear filters
                      </button>
                    </>
                  ) : (
                    <>
                      <ClipboardCheck className="mx-auto h-8 w-8 text-gray-400" />
                      <p className="mt-2 text-gray-500">No reviews found</p>
                      <p className="text-sm text-gray-400">
                        Reviews will appear here once applications are processed by AI.
                      </p>
                    </>
                  )}
                </td>
              </tr>
            ) : (
              data?.data.map((review) => (
                <tr key={review.id} className={`hover:bg-gray-50${review.staleFlag ? ' bg-amber-50' : ''}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100">
                        <ClipboardCheck className="h-5 w-5 text-purple-600" />
                      </div>
                      <div className="ml-4">
                        {review.candidateId ? (
                          <Link
                            to={`/candidates/${review.candidateId}`}
                            className="font-medium text-primary-600 hover:text-primary-700"
                          >
                            {review.candidateName || 'Unknown Candidate'}
                          </Link>
                        ) : (
                          <p className="font-medium text-gray-900">
                            {review.candidateName || 'Unknown Candidate'}
                          </p>
                        )}
                        <p className="text-xs text-gray-500">
                          {review.jobTitle || 'Unknown Job'}
                        </p>
                        {review.staleFlag && (
                          <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                            <AlertTriangle className="h-3 w-3" />
                            Needs Re-review
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1">
                      <StarRating rating={review.recommendation} />
                      <span
                        className={`inline-flex w-fit items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                          recommendationColors[review.recommendation]
                        }`}
                      >
                        {recommendationLabels[review.recommendation]}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <ScoreDisplay score={review.overallScore} />
                  </td>
                  <td className="px-6 py-4">
                    <p className="max-w-xs truncate text-sm text-gray-600">
                      {review.summary}
                    </p>
                  </td>
                  <td className="px-6 py-4">
                    {review.modelTier ? (
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                          review.modelTier === 'premium'
                            ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
                            : review.modelTier === 'standard'
                            ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'
                            : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                        }`}
                        title={review.modelUsed || 'Unknown model'}
                      >
                        {review.modelTier.charAt(0).toUpperCase() + review.modelTier.slice(1)}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    <span title={format(new Date(review.createdAt), 'PPpp')}>
                      {formatDistanceToNow(new Date(review.createdAt), {
                        addSuffix: true,
                      })}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => navigate(`/reviews/${review.id}`)}
                        className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                        title="View details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {data && data.pagination.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-200 bg-white px-6 py-3">
            <div className="text-sm text-gray-500">
              Showing{' '}
              <span className="font-medium">
                {(page - 1) * limit + 1}
              </span>{' '}
              to{' '}
              <span className="font-medium">
                {Math.min(page * limit, data.pagination.total)}
              </span>{' '}
              of <span className="font-medium">{data.pagination.total}</span>{' '}
              results
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handlePageChange(Math.max(1, page - 1))}
                disabled={page === 1}
                className="btn btn-secondary disabled:opacity-50"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() =>
                  handlePageChange(Math.min(data.pagination.totalPages, page + 1))
                }
                disabled={page === data.pagination.totalPages}
                className="btn btn-secondary disabled:opacity-50"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
